import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, FileText, Link as LinkIcon, Video, Download } from 'lucide-react';
import { getKnowledgeResources } from '../lib/api';

const TYPE_ICON = { pdf: FileText, doc: FileText, link: LinkIcon, video: Video };

export default function KnowledgePage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getKnowledgeResources();
        if (!cancelled) setResources(data.resources || []);
      } catch (e) {
        if (!cancelled) setError('Could not load knowledge resources.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-[#3D1F6E] dark:text-[#A78BFA] font-semibold mb-1">
          Resources
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
          Knowledge Base
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Interview preparation resources from your college
        </p>
      </div>

      {error && <p className="text-sm text-red-500" data-testid="knowledge-error">{error}</p>}

      {!loading && resources.length === 0 ? (
        <Card className="bg-card border-border card-shadow">
          <CardContent className="flex flex-col items-center py-10 gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center icon-bg-violet">
              <BookOpen className="w-7 h-7" />
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-base font-heading font-semibold">No resources yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Knowledge sources and preparation materials will be available here soon.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="knowledge-resource-grid">
          {resources.map((r) => (
            <ResourceCard key={r.resource_id} resource={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResourceCard({ resource }) {
  const Icon = TYPE_ICON[resource.source_type] || FileText;
  return (
    <Card className="bg-card border-border card-shadow" data-testid={`knowledge-resource-${resource.resource_id}`}>
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center icon-bg-violet shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <Badge variant="secondary" className="text-[10px] uppercase font-heading shrink-0">
            {resource.source_type}
          </Badge>
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-semibold font-heading truncate" title={resource.title}>
            {resource.title}
          </h3>
          {resource.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">{resource.description}</p>
          )}
        </div>
        {resource.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {resource.tags.map((t) => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary/60 text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        )}
        <a
          href={resource.file_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`knowledge-resource-open-${resource.resource_id}`}
          className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3D1F6E] dark:text-[#A78BFA] hover:underline"
        >
          <Download className="w-3.5 h-3.5" />
          {resource.source_type === 'link' ? 'Open link' : 'Download'}
        </a>
      </CardContent>
    </Card>
  );
}
